import React, { Component } from 'react';
import PropTypes from 'prop-types';
import ReportsQueueTable from './ReportsQueueTable';
import ReportsQueueHeader from './ReportsQueueHeader';
import ReportingGatewayApi from 'http-clients/reporting-gateway-api';
import reportsQueueTableUtils from './reportsQueueTableUtils';
import isEqual from 'lodash/isEqual';
import mainStyles from '../MainContainer.module.css';
import commonStrings from '../../resources/commonStrings';
import batchReportApi from '../../http-clients/batch-report-api';
import axios from 'axios';
import RLPError from '../ErrorPage/RLPError';
const CancelToken = axios.CancelToken;

const propTypes = {
  educatorBid: PropTypes.string.isRequired,
  partnerBid: PropTypes.string.isRequired,
  sessionToken: PropTypes.object.isRequired,
  transitionTime: PropTypes.number.isRequired,
};

const TABLE_REFRESH_INTERVAL = 30;
const TABLE_NOTIFY_REFRESH_INTERVAL = 2;

class ReportsQueueContainer extends Component {
  constructor(props) {
    super(props);
    this.state = {
      combinedSelectedRows: [],
      batchReportRequestIds: [],
      gatewayReportIds: [],
      selectAll: 'NONE_SELECTED', // Can be one of {ALL_SELECTED, NONE_SELECTED, INDETERMINATE}
      isRemoving: false,
      data: [],
      intervalId: window.setInterval(this.tick, 1000),
      isUpdating: false,
      isFirstDataLoad: true,
      error: null,
      cancelTokenSource: CancelToken.source(),
    };
    this.timer = TABLE_REFRESH_INTERVAL;
  }

  componentDidMount() {
    /*
     * This setTimeout exists to delay HTTP requests and subsequent rendering
     * of the table until the TabContainer transition is complete. Without this,
     * we experience very low FPS during the TabContainer's --selected-tab-position
     * transform.
     */
    setTimeout(() => {
      const requestGetParams = {
        ...this.props.sessionToken,
        ...{ cancelToken: this.state.cancelTokenSource.token },
      };

      let batchDone = false;
      let gatewayDone = false;
      const checkFirstDataLoad = () => {
        if (batchDone && gatewayDone) {
          this.setState({ isFirstDataLoad: false });
        }
      };

      /** BATCH REPORT API */
      this.getBatchReportData()
        .then((getBatchReportDataResponse) => {
          this.setState((prevState) => ({
            data: [...prevState.data, ...getBatchReportDataResponse],
          }));
        })
        .catch((error) => {
          // Log batch API errors but do not inform user - gateway API should still execute
          new RLPError(
            { payload: this.state },
            `Error requesting data from the batch report API for districtBid ${this.props.partnerBid} and educatorBid ${this.props.educatorBid}`,
            error
          ).log();
        })
        .finally(() => {
          batchDone = true;
          checkFirstDataLoad();
        });

      /** LEGACY - REPORTING GATEWAY API */
      ReportingGatewayApi.getReportQueue(this.props.educatorBid, requestGetParams)
        .then((response) => {
          this.dataGrooming(response.data);
          this.setState((prevState) => ({
            data: [...prevState.data, ...response.data],
          }));
        })
        .catch((err) => {
          if (axios.isCancel(err)) {
            console.warn('Request for reports queue data was cancelled because component unmounted');
          } else {
            this.setState({
              error: {
                type: 'refreshReportQueue',
                message:
                  'There was an error retrieving your reports. Please attempt a refresh before contacting Partner Services.',
              },
            });
            new RLPError(
              { payload: this.state },
              `Error requesting data from the reporting gateway API for educatorBid ${this.props.educatorBid}`,
              err
            ).log();
          }
        })
        .finally(() => {
          gatewayDone = true;
          checkFirstDataLoad();
        });
    }, this.props.transitionTime);
  }

  componentWillUnmount() {
    window.clearInterval(this.state.intervalId);
    this.state.cancelTokenSource.cancel('Request cancelled because component unmounted');
  }

  async deleteBatchReportData() {
    const educatorBid = this.props.educatorBid;
    const requestIds = this.state.batchReportRequestIds;
    const requestParams = this.props.sessionToken;
    await batchReportApi.deleteReports(educatorBid, requestIds, requestParams);
  }

  async getBatchReportData() {
    const educatorBid = this.props.educatorBid;
    const partnerBid = this.props.partnerBid;
    const requestParams = this.props.sessionToken;
    const reports = await batchReportApi.getReports(educatorBid, partnerBid, requestParams);
    const groomedReports = this.batchReportApiDataGrooming(reports.data.batch_reports);
    return groomedReports;
  }

  async getBatchReportDownlUrl(requestId) {
    const educatorBid = this.props.educatorBid;
    const requestParams = this.props.sessionToken;
    const response = await batchReportApi.getDownloadUrl(educatorBid, requestId, requestParams);
    return response.download_url;
  }

  // reporting api
  dataGrooming(data) {
    data.forEach((instance) => {
      instance.reportId = reportsQueueTableUtils.transformReportType(instance.reportId);
    });
  }

  createReportNameDescriptor(reportType, reportName, reportSubtype) {
    let descriptor;
    if (reportType === 'FAMILY_REPORT') {
      // ex: "Family Report - Students By School - Los Angeles Lakers Academy"
      // or: "Family Report Spanish - Students By School - Los Angeles Lakers Academy"
      descriptor = reportName.replace(/^Family Report Spanish - |^Family Report - /, '').replace(/_/g, ' ');
    } else if (reportType === 'DATA_EXPORT') {
      descriptor = reportName;
    }
    return descriptor;
  }

  setReportId(reportType, locale) {
    // set Report Type -> reportId (Family Report)
    switch (reportType) {
      case 'FAMILY_REPORT':
        if (locale && locale.startsWith('es')) {
          return 'Family Report Spanish';
        }
        return 'Family Report';
      case 'DATA_EXPORT':
        return 'Data Export';
      default:
        return 'unknown';
    }
  }

  // batch report api
  batchReportApiDataGrooming(data) {
    return data.map((report) => {
      return {
        canRetryYN: report.can_retry,
        createdDate: new Date(report.date_time_requested).toISOString(),
        createdAt: new Date(report.date_time_requested).getTime(),
        educatorBid: this.props.educatorBid,
        isBatchReportYN: true,
        outputFormat: `${report.download_format}/${report.report_format}`,
        partnerBid: report.partner_bid,
        reportFileSize: report.zip_file_size,
        reportId: this.setReportId(report.report_type, report.locale),
        // set Report Name
        reportNameDescriptor: this.createReportNameDescriptor(
          report.report_type,
          report.report_name,
          report.report_subtype
        ),
        reportProgress: report.report_progress,
        reportRequestStatusId: report.short_report_id,
        requestId: report.request_id,
        // set Status
        requestParams: this.props.sessionToken,
        requestStatus: report.report_status,
        retryCount: report.retry_count,
      };
    });
  }

  tick = () => {
    // The timer will be set to null when the table is refreshing to prevent duplicate refreshes
    if (this.timer === null) {
      return;
    }

    // Update the table to isUpdating if the user doesn't have checkboxes selected and the timer is at the specified time
    if (this.timer === TABLE_NOTIFY_REFRESH_INTERVAL) {
      this.setState({
        isUpdating: true,
      });
      this.timer = this.timer - 1;
      return;
    }

    // Count down the timer on each tick
    if (this.timer > 0) {
      this.timer = this.timer - 1;
      return;
    }

    // Refresh the table when the timer reaches 0. refreshTable already checks if checkboxes have been selected
    if (this.timer === 0) {
      void this.refreshTable();
    }
  };

  refreshTable = async () => {
    /*
     * If the timer is set to null, it should indicate that we're in
     * the middle of fetching new data, so there's no need to start fetching again.
     */
    if (this.timer !== null || this.state.isRemoving) {
      this.timer = null;
      const requestGetParams = {
        ...this.props.sessionToken,
        ...{ cancelToken: this.state.cancelTokenSource.token },
      };
      const getBatchReportDataResponse = await this.getBatchReportData();
      ReportingGatewayApi.getReportQueue(this.props.educatorBid, requestGetParams)
        .then((response) => {
          this.dataGrooming(response.data);
          const combinedData = [...response.data, ...getBatchReportDataResponse].map((item) => ({
            ...item,
          }));
          // Check for equality before re-assigning the state data
          if (!isEqual(combinedData, this.state.data)) {
            this.timer = TABLE_REFRESH_INTERVAL;
            const newState = {
              data: combinedData,
              selectAll: 'NONE_SELECTED',
              isUpdating: false,
            };
            /* We only want to clear the selectedRows array if the refresh was executed after row deletion */
            if (this.state.isRemoving) {
              newState.isRemoving = false;
              newState.combinedSelectedRows = [];
              newState.batchReportRequestIds = [];
              newState.gatewayReportIds = [];
            }

            this.setState(newState);
          } else {
            this.timer = TABLE_REFRESH_INTERVAL;
            this.setState({
              isUpdating: false,
            });
          }
        })
        .catch((err) => {
          if (axios.isCancel(err)) {
            console.error('Request to refresh reports queue data was cancelled because component unmounted');
          }
          // Error handling has been removed from here since the http-client.js
          // already addresses errors.
          this.timer = TABLE_REFRESH_INTERVAL;
          this.setState({
            isRemoving: false,
            isUpdating: false,
          });
        });
    }
  };
  /**
   * Handling toggle for deleting reports.
   * For separate calls to api - they both handle an array of report/request ids.
   * For handling toggling...
   * @param {*} requestId
   * @param {*} isBatchReportYN
   */

  toggleRow = (requestId, isBatchReportYN) => {
    // Create copies of the state arrays
    let newSelectedRows = [...this.state.combinedSelectedRows];
    let newBatchReportRequestIds = [...this.state.batchReportRequestIds];
    let newGatewayReportIds = [...this.state.gatewayReportIds];

    // Check if the requestId is already selected
    const reportIndex = newSelectedRows.indexOf(requestId);
    const batchReportIndex = newBatchReportRequestIds.indexOf(requestId);
    const gatewayReportIndex = newGatewayReportIds.indexOf(requestId);

    if (reportIndex === -1) {
      // Not selected yet → add it
      newSelectedRows.push(requestId);
      if (isBatchReportYN) {
        newBatchReportRequestIds.push(requestId);
      } else {
        newGatewayReportIds.push(requestId);
      }
    } else {
      // Already selected → remove it
      newSelectedRows.splice(reportIndex, 1);
      if (isBatchReportYN && batchReportIndex !== -1) {
        newBatchReportRequestIds.splice(batchReportIndex, 1);
      } else {
        newGatewayReportIds.splice(gatewayReportIndex, 1);
      }
    }

    // use combined array
    let selectedState;
    if (newSelectedRows.length === this.state.data.length) {
      selectedState = 'ALL_SELECTED';
    } else if (newSelectedRows.length > 0) {
      selectedState = 'INDETERMINATE';
    } else {
      selectedState = 'NONE_SELECTED';
    }
    // 3 states - batch report, gateway reports, combined reports
    this.setState({
      batchReportRequestIds: newBatchReportRequestIds,
      gatewayReportIds: newGatewayReportIds,
      combinedSelectedRows: newSelectedRows,
      selectAll: selectedState,
    });
  };

  removeSelected = async () => {
    if (!this.state.combinedSelectedRows.length) return;
    this.setState({
      isRemoving: true,
      error: null,
    });

    // handle new batch reports removal
    if (this.state.batchReportRequestIds.length > 0) {
      await this.deleteBatchReportData();
      void this.refreshTable();
    }
    // handle gateway reports removal
    if (this.state.gatewayReportIds.length > 0) {
      ReportingGatewayApi.deleteReports(this.state.gatewayReportIds, this.props.sessionToken)
        .then(() => {
          void this.refreshTable();
        })
        .catch((err) => {
          new RLPError({ payload: this.state }, err).log();
          this.setState({
            isRemoving: false,
            error: {
              type: 'removeSelected',
            },
          });
        });
    }
  };

  closeErrorPopover = (ref, setFocus = true) => {
    this.setState({ error: null });
    if (ref && setFocus) {
      ref.current.focus();
    }
  };

  toggleSelectAll = () => {
    let newSelected = [];
    let batchRequestIds = [];
    let gatewayReportIds = [];

    if (this.state.selectAll === 'NONE_SELECTED') {
      this.state.data.forEach((row) => {
        if (row.isBatchReportYN) {
          newSelected.push(row.requestId);
          batchRequestIds.push(row.requestId);
        } else {
          newSelected.push(row.reportRequestStatusId);
          gatewayReportIds.push(row.reportRequestStatusId);
        }
      });
    }

    this.setState({
      batchReportRequestIds: batchRequestIds,
      gatewayReportIds: gatewayReportIds,
      combinedSelectedRows: newSelected,
      selectAll: this.state.selectAll === 'NONE_SELECTED' ? 'ALL_SELECTED' : 'NONE_SELECTED',
    });
  };

  render() {
    return (
      <>
        <h1 className={mainStyles['page-title']}>{commonStrings.queue.pageTitle}</h1>
        <ReportsQueueHeader />
        <ReportsQueueTable
          data={this.state.data}
          selectedRows={this.state.combinedSelectedRows}
          selectAll={this.state.selectAll}
          toggleRow={this.toggleRow}
          toggleSelectAll={this.toggleSelectAll}
          isUpdating={this.state.isUpdating}
          isFirstDataLoad={this.state.isFirstDataLoad}
          error={this.state.error}
          removeSelected={this.removeSelected}
          isRemoving={this.state.isRemoving}
          closeErrorPopover={this.closeErrorPopover}
          onRetrySuccess={this.refreshTable}
        />
        <footer className={mainStyles['confidentiality-statement']}>{commonStrings.pageFooter}</footer>
      </>
    );
  }
}

ReportsQueueContainer.propTypes = propTypes;
export { ReportsQueueContainer }; // unit tests only
export default ReportsQueueContainer;
